<script setup lang="ts">
/**
 * Fil d'Ariane spatial Univers > Galaxie > Systeme > Planete.
 *
 * Affiche le contexte spatial de la route courante avec liens cliquables
 * a chaque niveau. Le dernier segment = position actuelle (non cliquable).
 *
 * Visible uniquement sur les routes "spatiales" (planet, cosmos/g/s,
 * museum, academy). Sur les vues meta (inbox, recherches, classement...)
 * le composant rend vide -- ces routes n'ont pas de contexte spatial.
 *
 * Mobile : layout horizontal compact qui scroll si necessaire (pas de
 * modal/sheet pour l'instant -- les segments sont court).
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useSessionStore } from '@/stores/session.store';

interface Segment {
  label: string;
  /** null = segment courant, non cliquable. */
  to: string | null;
}

const route = useRoute();
const session = useSessionStore();

const segments = computed<Segment[]>(() => {
  if (!session.inUniverse) return [];

  const r = route;
  const items: Segment[] = [];

  // Detection du contexte spatial depuis la route + la session.
  // `cosmos-*` exposent directement g/s en params. Pour les vues planete,
  // on lit session.lastPlanet (le setLastPlanetContext est appele a chaque
  // visite, donc fiable des qu'on est sur la planete).
  let galaxy: number | null = null;
  let system: number | null = null;
  let leaf: string | null = null;

  switch (r.name) {
    case 'cosmos':
      // Vue cosmos sans focus -- on n'affiche que "Univers".
      break;
    case 'cosmos-galaxy':
      // Les routes /cosmos/g/:n/s/:m/p/:p utilisent les params n/m/p
      // (cf router/game.ts) -- PAS g/s/p. Bug historique : on lisait
      // r.params.g/s/p qui restaient null -> breadcrumb invisible sur
      // les vues cosmos (seul "Univers" survivait, filtre L115).
      galaxy = numberParam(r.params.n);
      break;
    case 'cosmos-system':
      galaxy = numberParam(r.params.n);
      system = numberParam(r.params.m);
      break;
    case 'cosmos-planet':
      galaxy = numberParam(r.params.n);
      system = numberParam(r.params.m);
      leaf = `Planète ${numberParam(r.params.p) ?? '?'}`;
      break;
    case 'planet':
    case 'planet-legacy': {
      const lp = session.lastPlanet;
      const pid = numberParam(r.params.id);
      if (lp && lp.planetId === pid) {
        galaxy = lp.galaxyNumber;
        system = lp.solarSystemNumber;
        leaf = lp.planetName ? `🪐 ${lp.planetName}` : 'Planète';
      } else {
        leaf = 'Planète';
      }
      break;
    }
    case 'museum':
    case 'academy': {
      const lp = session.lastPlanet;
      const pid = numberParam(r.params.planetId);
      if (lp && lp.planetId === pid) {
        galaxy = lp.galaxyNumber;
        system = lp.solarSystemNumber;
      }
      leaf = r.name === 'museum' ? 'Musée' : 'Académie';
      break;
    }
    case 'fleet':
    case 'battle': {
      // Pas de contexte planete fiable -- on remonte juste a l'univers +
      // libelle de la vue.
      leaf = r.name === 'fleet' ? 'Flotte' : 'Bataille';
      break;
    }
    default:
      // Vues non spatiales : on rend rien (segments vide -> v-if cache tout).
      return [];
  }

  // === Construction effective ===
  // "Univers" -> /cosmos : ouvre la vue 3D globale avec les galaxies
  // accessibles (filtrees par le brouillard de guerre serveur, cf §37).
  // Anciennement pointait vers '/' = HomeView, ce qui rompait la
  // navigation spatiale (le joueur tombait sur le picker d'univers).
  items.push({ label: 'Univers', to: '/cosmos' });

  if (galaxy != null) {
    items.push({ label: `Galaxie ${galaxy}`, to: `/cosmos/g/${galaxy}` });
  }
  if (galaxy != null && system != null) {
    items.push({ label: `Système ${system}`, to: `/cosmos/g/${galaxy}/s/${system}` });
  }
  if (leaf) {
    items.push({ label: leaf, to: null });
  } else if (r.name === 'cosmos-galaxy' && galaxy != null) {
    // La galaxie est le segment courant -> on rend le dernier item non cliquable.
    const last = items[items.length - 1];
    if (last) last.to = null;
  } else if (r.name === 'cosmos-system' && system != null) {
    const last = items[items.length - 1];
    if (last) last.to = null;
  }

  // Si on n'a que "Univers" en segment -> pas de breadcrumb significatif,
  // on rend vide (eviter le bandeau pour rien sur /cosmos sans focus).
  if (items.length <= 1) return [];

  return items;
});

function numberParam(p: string | string[] | undefined): number | null {
  if (Array.isArray(p)) p = p[0];
  if (typeof p !== 'string') return null;
  const n = parseInt(p, 10);
  return Number.isFinite(n) ? n : null;
}
</script>

<template>
  <nav v-if="segments.length > 0" class="spatial-breadcrumb" aria-label="Fil d'Ariane spatial">
    <template v-for="(s, i) in segments" :key="i">
      <span v-if="i > 0" class="sb-sep" aria-hidden="true">›</span>
      <router-link
        v-if="s.to"
        :to="s.to"
        class="sb-segment sb-link"
      >{{ s.label }}</router-link>
      <span v-else class="sb-segment sb-current">{{ s.label }}</span>
    </template>
  </nav>
</template>

<style lang="scss" scoped>
.spatial-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  padding: 6px 16px;
  font-size: $font-size-sm;
  color: $text-secondary;
  background: $bg-secondary;
  border-bottom: 1px solid $border-subtle;
  overflow-x: auto;
  white-space: nowrap;
  @include scrollbar-thin;

  @media (max-width: 767.99px) {
    padding: 6px 12px;
    font-size: $font-size-xs;
  }
}

.sb-sep {
  color: $text-muted;
  user-select: none;
}

.sb-segment {
  text-decoration: none;
  font-family: $font-mono;
}

.sb-link {
  color: $text-secondary;
  cursor: pointer;
  transition: color $duration-fast $easing-default;

  &:hover {
    color: $color-neon-cyan;
  }
}

.sb-current {
  color: $text-primary;
  font-weight: $font-weight-bold;
}
</style>
